import React, { useMemo } from "react"
import cn from "../../../utils/classes"
import { FontSizes, FontWeights, Label, TextColors } from "./Label"
import { Justify } from "./ButtonWrapper"
import Icon, { type IconType, type Size } from "./Icon"

export interface IconWithCaptionProps {
  disabled?: boolean
  caption: string
  icon: IconType
  size?: Size
  textLocation?: "top" | "bottom" | "right" | "left"
  textColor?: TextColors
  truncate?: boolean
  justification?: Justify
  fontWeight?: FontWeights
  fontSize?: FontSizes
  wrap?: boolean
  textCentered?: boolean
  ariaRole?: string
  isButtonChild?: boolean
  iconStyleOverrides?: string
  labelStyleOverrides?: string
  divStyleOverrides?: string
}

/**
 * `IconWithCaption` is a customizable component that includes an icon and a caption.
 *
 * @component
 * @example
 * // Example usage of IconWithCaption
 * <IconWithCaption caption="Home" icon="homeIcon" textColor="black" ariaRole="button" onClick={() => console.log("Icon clicked!")}/>
 *
 * @param {object} props - The properties that define the IconWithCaption component.
 * @param {boolean} [props.disabled=false] - If true, the component will be disabled.
 * @param {string} props.caption - The caption of the component.
 * @param {IconType} props.icon - The type of the icon to be included in the component.
 * @param {Size} [props.size] - The size of the icon.
 * @param {("top"|"bottom"|"right"|"left")} [props.textLocation='bottom'] - The location of the text relative to the icon.
 * @param {TextColors} [props.textColor='black'] - The color of the text.
 * @param {boolean} [props.truncate=false] - If true, the text will be truncated.
 * @param {Justify} [props.justification='center'] - The justification of the text.
 * @param {FontWeights} [props.fontWeight='normal'] - The font weight of the text.
 * @param {FontSizes} [props.fontSize='medium'] - The font size of the text.
 * @param {boolean} props.wrap - If true, the text will wrap.
 * @param {boolean} [props.textCentered=false] - If true, the text will be centered.
 * @param {string} props.ariaRole - The aria role attribute for the component for accessibility.
 * @param {boolean} [props.isButtonChild=false] - If true, the component will be styled as a button child.
 * @param {string} [props.iconStyleOverrides] - Override styles for the icon.
 * @param {string} [props.labelStyleOverrides] - Override styles for the label.
 * @param {string} [props.divStyleOverrides] - Override styles for the div wrapper.
 *
 * @returns {React.Component} The IconWithCaption component.
 */
const IconWithCaption: React.FC<IconWithCaptionProps> = ({
  disabled = false,
  caption,
  icon,
  size,
  textLocation = "bottom",
  textColor = "black",
  truncate = false,
  justification = "center",
  fontWeight = "normal",
  fontSize = "medium",
  wrap,
  textCentered = false,
  ariaRole,
  isButtonChild = false,
  iconStyleOverrides,
  labelStyleOverrides,
  divStyleOverrides,
}) => {
  const flexStyle = ["right", "left"].includes(textLocation)
    ? "flex-row space-x-2"
    : "flex-col"
  const justificationStyle = `justify-${justification}`
  const disabledStyle = disabled ? "opacity-50 cursor-not-allowed" : ""
  const buttonStyle = isButtonChild ? "cursor-pointer" : ""
  const divDefaultStyle = `flex ${flexStyle} items-center ${justificationStyle} ${disabledStyle}`

  const iconClassName = useMemo(
    () => cn(buttonStyle, iconStyleOverrides),
    [buttonStyle, iconStyleOverrides],
  )
  const labelClassName = useMemo(
    () => cn(buttonStyle, labelStyleOverrides),
    [buttonStyle, labelStyleOverrides],
  )
  const divClassName = useMemo(
    () => cn(divDefaultStyle, divStyleOverrides),
    [divDefaultStyle, divStyleOverrides],
  )

  const iconComponent = (
    <Icon iconType={icon} size={size} styleOverrides={iconClassName} />
  )
  const labelComponent = (
    <Label
      text={caption}
      htmlFor={caption}
      color={textColor}
      truncate={truncate}
      fontWeight={fontWeight}
      size={fontSize}
      wrap={wrap}
      textCentered={textCentered}
      styleOverrides={labelClassName}
    />
  )
  let firstComponent
  let secondComponent

  if (["top", "left"].includes(textLocation)) {
    firstComponent = labelComponent
    secondComponent = iconComponent
  } else {
    firstComponent = iconComponent
    secondComponent = labelComponent
  }

  return (
    <div className={divClassName} role={ariaRole}>
      {firstComponent}
      {secondComponent}
    </div>
  )
}

export default IconWithCaption
